import {useState} from 'react';

import {getEffectivePermissions} from '@afriex/core/utils';

import {
  Badge,
  Table,
  TableSkeleton,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import {
  ExpandButton,
  ExpandedPanel,
  PanelSection,
} from '@/screens/Settings/ExpandedRowPanel';
import type {BusinessAPIKeyResponse} from '@/types/AfriexBusiness';
import * as dateUtils from '@/utils/date';

type Props = {
  keys: BusinessAPIKeyResponse[];
  isLoading?: boolean;
  hasFailed?: boolean;
};

// What the key can call: its allow list minus its deny list, computed the way
// APITokenProvider enforces it, and named as in the server's refusal logs.
// Undefined for a key saved before scopes existed, which may call every route.
const getAllowedPermissions = (
  permissions: BusinessAPIKeyResponse['permissions'],
) =>
  permissions &&
  [
    ...getEffectivePermissions({
      userPermissions: permissions,
      rolePermissions: {allow: [], deny: []},
    }),
  ].sort();

// One entry per resource (the part before the first dot), e.g.
// CUSTOMER: CREATE, READ, so a broad key stays a few lines tall.
const groupByResource = (permissions: string[]) => {
  const byResource = new Map<string, string[]>();
  for (const permission of permissions) {
    const [resource, ...action] = permission.split('.');
    byResource.set(resource, [
      ...(byResource.get(resource) ?? []),
      action.join('.'),
    ]);
  }
  return [...byResource];
};

const KeyRow = ({apiKey}: {apiKey: BusinessAPIKeyResponse}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const allowed = getAllowedPermissions(apiKey.permissions);

  return (
    <>
      <Tr>
        <Td className="font-medium px-4">{apiKey.name}</Td>
        <Td className="font-mono text-xs break-all">{apiKey.key}</Td>
        <Td>{apiKey.target}</Td>
        <Td>
          <Badge
            label={apiKey.isActive ? 'Active' : 'Inactive'}
            colorScheme={apiKey.isActive ? 'successful' : 'failed'}
          />
        </Td>
        <Td>
          {!allowed && 'All (legacy key)'}
          {allowed?.length === 0 && 'None'}
          {!!allowed?.length && (
            <div className="flex items-center gap-2">
              <ExpandButton
                isExpanded={isExpanded}
                onToggle={() => setIsExpanded(!isExpanded)}
                label={`${apiKey.name} permissions`}
              />
              {allowed.length} allowed
            </div>
          )}
        </Td>
        <Td>
          {apiKey.createdAt
            ? dateUtils.DDMonYYYY(apiKey.createdAt.toString())
            : '-'}
        </Td>
        <Td>
          {apiKey.lastUsedAt
            ? dateUtils.DDMonYYYY(apiKey.lastUsedAt.toString(), true)
            : 'Never'}
        </Td>
      </Tr>

      {isExpanded && !!allowed?.length && (
        <Tr enableHover={false}>
          <Td colSpan={7} className="p-0 m-0">
            <ExpandedPanel>
              <PanelSection title="Permissions">
                <ul className="md:columns-2 xl:columns-3 gap-x-10 font-mono text-xs">
                  {groupByResource(allowed).map(([resource, actions]) => (
                    <li key={resource} className="mb-1 break-inside-avoid">
                      <span className="font-semibold">{resource}</span>:{' '}
                      {actions.join(', ')}
                    </li>
                  ))}
                </ul>
              </PanelSection>
            </ExpandedPanel>
          </Td>
        </Tr>
      )}
    </>
  );
};

const BusinessAPIKeysTable = ({keys, isLoading, hasFailed}: Props) => {
  if (isLoading) {
    return <TableSkeleton />;
  }

  if (hasFailed) {
    return (
      <p className="py-6 text-center text-sm text-failed-500">
        Could not load the API keys. Try reloading the page.
      </p>
    );
  }

  if (!keys.length) {
    return (
      <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        This business has no API keys.
      </p>
    );
  }

  return (
    <Table className="overflow-hidden">
      <Thead>
        <Tr enableHover={false}>
          <Th className="px-4">Name</Th>
          <Th>Key</Th>
          <Th>Environment</Th>
          <Th>Status</Th>
          <Th>Permissions</Th>
          <Th>Created</Th>
          <Th>Last Used</Th>
        </Tr>
      </Thead>
      <TBody>
        {keys.map((apiKey) => (
          <KeyRow key={apiKey.id} apiKey={apiKey} />
        ))}
      </TBody>
    </Table>
  );
};

export default BusinessAPIKeysTable;
